import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Check, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput, TextArea } from "./kit";

const APP_STATUSES = ["New", "Reviewing", "Shortlisted", "Rejected"];

const statusColor = {
  New: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
  Reviewing: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  Shortlisted: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  Rejected: "bg-red-400/15 text-red-300 border-red-400/30",
};

function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/questions").then(({ data }) => setQuestions(data));
  useEffect(() => { load(); }, []);

  const save = async (q) => {
    try {
      const payload = { question: q.question, options: q.options, correct_option: Number(q.correct_option) };
      if (q.id) await api.put(`/admin/questions/${q.id}`, payload);
      else await api.post("/admin/questions", payload);
      toast.success("Question saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (q) => {
    await api.delete(`/admin/questions/${q.id}`);
    toast.success("Question removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="recruitment-questions">
      <Card title="Qualifying Questions" desc="Applicants must pick the qualifying answer to proceed. A wrong answer stops the form immediately with a polite message.">
        <div className="space-y-4">
          {questions.map((q, qi) => (
            <div key={q.id} className="rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-5" data-testid={`question-row-${qi}`}>
              {editing?.id === q.id ? (
                <>
                  <Field label="Question"><TextInput value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} /></Field>
                  <Field label="Options (one per line)">
                    <TextArea value={editing.options.join("\n")} onChange={(e) => setEditing({ ...editing, options: e.target.value.split("\n").filter(Boolean) })} />
                  </Field>
                  <Field label="Qualifying answer">
                    <select className="admin-input" value={editing.correct_option} onChange={(e) => setEditing({ ...editing, correct_option: Number(e.target.value) })}>
                      {editing.options.map((o, i) => <option key={i} value={i}>{o}</option>)}
                    </select>
                  </Field>
                  <div className="flex gap-3 mt-4">
                    <button className="btn btn-sm btn-primary" onClick={() => save(editing)}>Save</button>
                    <button className="btn btn-sm btn-outline-light" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="text-sm text-white font-semibold">{qi + 1}. {q.question}</div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setEditing({ ...q })} className="text-xs font-bold text-cyan-400 hover:text-cyan-300">Edit</button>
                      <button onClick={() => remove(q)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete question" data-testid={`question-delete-${qi}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {q.options.map((o, oi) => (
                      <div key={oi} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${oi === q.correct_option ? "bg-emerald-400/10 text-emerald-300" : "text-slate-400"}`}>
                        {oi === q.correct_option ? <Check size={13} /> : <X size={13} className="opacity-40" />}
                        {o} {oi === q.correct_option && <span className="ml-auto text-[10px] font-bold uppercase">Qualifying</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <button
          className="btn btn-sm btn-outline-light mt-5"
          onClick={() => setEditing({ question: "", options: ["", ""], correct_option: 0 })}
          data-testid="question-add-button"
        >
          <Plus size={14} /> Add question
        </button>
      </Card>
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/applications").then(({ data }) => setApps(data));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    setApps((arr) => arr.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    await api.patch(`/admin/applications/${id}`, patch);
    toast.success("Application updated");
  };

  return (
    <div className="space-y-4" data-testid="recruitment-applications">
      {apps.map((a) => (
        <div key={a.id} className="bg-[#0E1642] border border-white/10 rounded-2xl p-6" data-testid={`application-row-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display font-extrabold text-white">{a.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{a.email} · {a.phone || "no phone"} · {a.years || "?"} yrs experience</div>
              <div className="text-xs text-cyan-300 font-semibold mt-1">Applying for: {a.role || "General"}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</span>
              <select
                value={a.status || "New"}
                onChange={(e) => update(a.id, { status: e.target.value })}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border outline-none cursor-pointer ${statusColor[a.status] || statusColor.New}`}
                data-testid={`application-status-${a.id}`}
              >
                {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
                aria-label="Toggle details"
                data-testid={`application-expand-${a.id}`}
              >
                <ChevronDown size={17} className={`transition-transform duration-300 ${expanded === a.id ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>

          {expanded === a.id && (
            <div className="mt-5 space-y-5">
              {a.bio && <p className="text-sm text-slate-300 leading-relaxed border-l-2 border-white/10 pl-4">{a.bio}</p>}
              <div>
                <div className="eyebrow text-[9px] text-cyan-400 mb-2">Their answers</div>
                <div className="space-y-1.5">
                  {(a.answers || []).map((ans, i) => (
                    <div key={i} className={`flex flex-wrap items-center gap-2 text-xs rounded-lg px-3 py-2 ${ans.passed ? "bg-emerald-400/10" : "bg-red-400/10 text-red-300"}`}>
                      <span className="text-slate-400">{ans.question}:</span>
                      <span className="font-semibold text-slate-200">{ans.answer}</span>
                      {!ans.passed && <X size={13} />}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow text-[9px] text-cyan-400 mb-2">Recruiter notes (private)</div>
                <TextArea rows={3} defaultValue={a.notes || ""} onBlur={(e) => update(a.id, { notes: e.target.value })} placeholder="Internal notes about this applicant..." data-testid={`application-notes-${a.id}`} />
              </div>
            </div>
          )}
        </div>
      ))}
      {apps.length === 0 && (
        <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-10 text-center text-slate-500 text-sm">
          No applications yet. Applicants who pass your qualifying questions appear here.
        </div>
      )}
    </div>
  );
}

export default function Recruitment() {
  const [tab, setTab] = useState("questions");
  return (
    <div className="space-y-6" data-testid="admin-recruitment">
      <div className="flex gap-2">
        {[
          { key: "questions", label: "Qualifying Questions" },
          { key: "applications", label: "Applications" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${tab === t.key ? "bg-[#00BFFF] text-[#06122B]" : "bg-[#0E1642] text-slate-300 border border-white/10 hover:border-cyan-400/40"}`}
            data-testid={`recruitment-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "questions" && <QuestionsTab />}
      {tab === "applications" && <ApplicationsTab />}
    </div>
  );
}
